import OliverText from "@builder/components/general/OliverText";
import "./style.scss";
import SimpleCheckbox from "@builder/components/general/SimpleCheckbox";
import SkeletonText from "@builder/components/general/SkeletonText";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector, useAppDispatch } from "@builder/store/store";
import { ActionEditorItem, updateStepperActions } from "@builder/store/slices/actionBuilderSlice";
import { ActionType } from "@common/build/types";
import { useGetUsersQuery } from "@builder/store/slices/dataApiSlices/userApiSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetRolesQuery } from "@builder/store/slices/dataApiSlices/rolesApiSlice";
import SingleSelect from "@builder/components/general/SelectInputNew/SingleSelect";
import { User } from "@builder/types/Users.type";

enum SwitchUserOption {
    DIALOG = "switch_user_dialog",
    ROLES = "switch_user_roles",
    SPECIFIC = "switch_user_specific"
}

const getSelectedRoles = (ids: string[]) => {
    if (!ids) return {};

    const selectedRoles: Record<string, boolean> = {};
    ids.map(item => (selectedRoles[item] = true));
    return selectedRoles;
};

const SwitchUser = () => {
    const { id } = useAppSelector(state => state.actionBuilder.current);
    const actionStep = useAppSelector(state => state.actionBuilder.actionsEditor.find(item => item.id === id));
    // const [loading, setLoading] = useState(false);
    // const [roles, setRoles] = useState<Role[]>([]);
    const { isOrg, company, data } = useAppSelector(state => state.storeData);
    const selectedCompany = data?.company;
    const [selectedRoles, setSelectedRoles] = useState<Record<string, boolean>>(getSelectedRoles(actionStep?.action?.data?.ids) || {});
    const dispatch = useAppDispatch();

    const { data: users } = useGetUsersQuery(
        company?._id && selectedCompany?._id ? { companyId: selectedCompany._id, ...(isOrg && { organizationId: company._id }) } : skipToken
    );

    const { data: roles, isLoading: isLoadingRoles } = useGetRolesQuery(
        company?._id && selectedCompany?._id ? { companyId: selectedCompany._id, ...(isOrg && { organizationId: company._id }) } : skipToken
    );

    const selectedUser = useMemo(() => {
        if (!users) return null;
        return users.find(user => user._id === actionStep?.action?.data?.id);
    }, [users, actionStep]);

    const getInitialOption = () => {
        if (!actionStep?.action?.type) return "";
        switch (actionStep.action.type) {
            case ActionType.SWITCH_USER_DIALOG:
                return SwitchUserOption.DIALOG;
            case ActionType.SWITCH_USER_ROLE:
                return SwitchUserOption.ROLES;
            case ActionType.SWITCH_USER_SPECIFIC:
                return SwitchUserOption.SPECIFIC;
            default:
                return "";
        }
    };

    const [selectedOption, setSelectedOption] = useState<string>(getInitialOption());

    const toggleSelection = (roleId: string) => {
        setSelectedRoles(prevSelectedRoles => ({
            ...prevSelectedRoles,
            [roleId]: !prevSelectedRoles[roleId]
        }));
    };

    const handleOptionSelect = (option: string) => {
        setSelectedOption(option);
    };

    const handleUserSelect = (user: User | null) => {
        if (!user) return;
        const action = {
            type: ActionType.SWITCH_USER_SPECIFIC,
            data: { id: user._id }
        };

        const updatedStep = { ...actionStep };
        updatedStep.action = action;
        if (JSON.stringify(updatedStep) !== JSON.stringify(actionStep)) {
            dispatch(updateStepperActions({ id: id, item: updatedStep as ActionEditorItem }));
        }
    };

    useEffect(() => {
        let action;

        if (selectedOption === SwitchUserOption.DIALOG) {
            action = {
                type: ActionType.SWITCH_USER_DIALOG
            };
        } else if (selectedOption === SwitchUserOption.ROLES) {
            const selectedIds = Object.keys(selectedRoles).filter(key => selectedRoles[key]);
            action = {
                type: ActionType.SWITCH_USER_ROLE,
                data: { ids: selectedIds }
            };
        }

        if (action) {
            const updatedStep = { ...actionStep };
            updatedStep.action = action;
            if (JSON.stringify(updatedStep) !== JSON.stringify(actionStep)) {
                dispatch(updateStepperActions({ id: id, item: updatedStep as ActionEditorItem }));
            }
        }
    }, [selectedOption, selectedRoles, actionStep, id, dispatch, users]);

    return (
        <div className="function-add-cart-note-wrapper">
            <OliverText text="How do you want to switch user?" size="xs" weight="medium-heavy" translatePrefix="popup.actionBuilder." />
            <div className="function-add-cart-note">
                <div className="function-checkbox-wrapper" onClick={() => handleOptionSelect(SwitchUserOption.DIALOG)}>
                    <div className="function-checkbox-label-wrapper">
                        <OliverText text="Open switch user dialog box" size="xs" weight="medium-heavy" translatePrefix="popup.actionBuilder." />
                        <OliverText
                            text="Allow the user to switch form one account to another"
                            size="xs"
                            weight="light"
                            translatePrefix="popup.actionBuilder."
                        />
                    </div>
                    <SimpleCheckbox
                        callback={() => handleOptionSelect(SwitchUserOption.DIALOG)}
                        checked={selectedOption === SwitchUserOption.DIALOG}
                        label=""
                        value=""
                        custom={false}
                    />
                </div>
                <div className="function-checkbox-wrapper" onClick={() => handleOptionSelect(SwitchUserOption.ROLES)}>
                    <div className="function-checkbox-label-wrapper">
                        <OliverText text="Switch to specific role" size="xs" weight="medium-heavy" translatePrefix="popup.actionBuilder." />
                        <OliverText
                            text="Allow the user to switch from one owner account to another"
                            size="xs"
                            weight="light"
                            translatePrefix="popup.actionBuilder."
                        />
                    </div>
                    <SimpleCheckbox
                        callback={() => handleOptionSelect(SwitchUserOption.ROLES)}
                        checked={selectedOption === SwitchUserOption.ROLES}
                        label=""
                        value=""
                        custom={false}
                    />
                </div>
                <div className="function-checkbox-wrapper" onClick={() => handleOptionSelect(SwitchUserOption.SPECIFIC)}>
                    <div className="function-checkbox-label-wrapper">
                        <OliverText text="Switch to specific user" size="xs" weight="medium-heavy" translatePrefix="popup.actionBuilder." />
                        <OliverText
                            text="Allow to switch to the specific user chosen here"
                            size="xs"
                            weight="light"
                            translatePrefix="popup.actionBuilder."
                        />
                    </div>
                    <SimpleCheckbox
                        callback={() => handleOptionSelect(SwitchUserOption.SPECIFIC)}
                        checked={selectedOption === SwitchUserOption.SPECIFIC}
                        label=""
                        value=""
                        custom={false}
                    />
                </div>
                {selectedOption === SwitchUserOption.ROLES && (
                    <>
                        <OliverText
                            text="Select which user roles can proceed"
                            size="s"
                            weight="medium-heavy"
                            translatePrefix="popup.actionBuilder."
                        />
                        <div className="function-add-cart-note">
                            {isLoadingRoles ? (
                                <SkeletonText />
                            ) : (
                                <>
                                    {roles && roles.length > 0
                                        ? roles.map(item => (
                                              <div key={item._id} className="function-checkbox-wrapper" onClick={() => toggleSelection(item._id)}>
                                                  <div className="function-checkbox-label-wrapper">
                                                      <OliverText text={item.name} size="xs" weight="medium-heavy" />
                                                      <OliverText text={`Allow ${item.name.toLowerCase()}s to proceed`} size="xs" weight="light" />
                                                  </div>
                                                  <SimpleCheckbox
                                                      callback={toggleSelection}
                                                      checked={selectedRoles?.[item._id] || false}
                                                      label=""
                                                      value={item._id}
                                                      custom={true}
                                                  />
                                              </div>
                                          ))
                                        : null}
                                </>
                            )}
                        </div>
                    </>
                )}

                {selectedOption === SwitchUserOption.SPECIFIC && (
                    <>
                        <OliverText text="Select which user can proceed" size="s" weight="medium-heavy" translatePrefix="popup.actionBuilder." />

                        <SingleSelect
                            options={users ? users.filter(user => !user.isDeleted) : []}
                            value={selectedUser ?? null}
                            onChange={value => handleUserSelect(value)}
                            placeholder="Select user"
                            getOptionLabel={user => user.firstName || user.lastName || user.email || ""}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default SwitchUser;
